import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { createAppAsyncThunk } from '../../../../common/thunk';
import { OnboardingStepState } from '../../onboardingSlice';
import { ChargebeeAddonType } from './Feature/Addons/Addons';
import PaymentService, { ChargebeeSubscriptionItemRequestAttr } from '../../../../services/payment.service';
import ItemPriceType from '../../../../common/interfaces/Chargebee/ItemPriceType';
import httpErrorHandling from '../../../../utils/httpErrorHandling';

export const getPrices = createAppAsyncThunk('publications/prices', () => {
  return PaymentService.getPrices();
});

export const verifyPayment = createAppAsyncThunk('publication/verify', () => {
  return PaymentService.verifyPayment();
});

export const checkout = createAppAsyncThunk('publication/checkout', (subscriptionItems: ChargebeeSubscriptionItemRequestAttr[]) => {
  return PaymentService.checkout(subscriptionItems);
});

interface PublicationState extends OnboardingStepState {
  professionalId: string;
  selectedPlanId: string;
  addons: ChargebeeAddonType[];
  planPrices: ItemPriceType[];
  addonPrices: ChargebeeAddonType[];
  externalCheckoutLink: string;
}

const initialState: PublicationState = {
  professionalId: '',
  selectedPlanId: '',
  addons: [],
  planPrices: [],
  addonPrices: [],
  externalCheckoutLink: '',
  inProgress: false,
  isDone: false,
  doRedirectFurther: false,
  error: '',
};
export const publicationSlice = createSlice({
  name: 'publication',
  initialState,
  reducers: {
    setPublicationStateDefault: () => {
      return { ...initialState };
    },
    startProcessingPublication: state => {
      state.inProgress = true;
    },
    selectPlan: (state, action: PayloadAction<string>) => {
      state.selectedPlanId = action.payload;
    },
    selectAddons: (state, action: PayloadAction<ChargebeeAddonType[]>) => {
      state.addons = action.payload;
    },
    submitPublication: state => {
      state.inProgress = false;
      state.isDone = true;
      state.doRedirectFurther = false;
    },
  },
  extraReducers: builder => {
    builder.addCase(verifyPayment.fulfilled, state => {
      state.doRedirectFurther = true;
    });
    builder.addCase(checkout.fulfilled, (state, action) => {
      state.externalCheckoutLink = action.payload.url;
    });
    builder.addCase(checkout.pending, state => {
      state.externalCheckoutLink = '';
      state.error = '';
    });
    builder.addCase(checkout.rejected, (state, action) => {
      state.error = httpErrorHandling(action);
    });
    builder.addCase(getPrices.fulfilled, (state, action: PayloadAction<ItemPriceType[]>) => {
      state.planPrices = action.payload.filter(it => it.item_type === 'plan');
      state.addonPrices = action.payload.filter(it => it.item_type === 'addon');
    });
  },
});

export const { setPublicationStateDefault, startProcessingPublication, submitPublication, selectPlan, selectAddons } =
  publicationSlice.actions;

export default publicationSlice.reducer;
